/**
 * Display helpers for Immo 360 import-history consultation (read-only).
 */

import {
  adminImportProvenanceSourceLabel,
  adminRegistryActivityTypeLabel,
  formatAdminDate,
  formatAdminProspectCentsLabel,
  formatAdminProspectLandArea,
  formatAdminProspectUnits,
  formatAdminProspectUnavailable,
} from './adminProspectDisplay.js';
import {
  formatImportPreviewDateOnly,
  importPreviewTopFieldLabel,
} from './prospectImportPreviewFormat.js';

export function mergeImportHistoryEntries(existing, incoming) {
  const current = Array.isArray(existing) ? existing : [];
  const next = Array.isArray(incoming) ? incoming : [];
  if (!next.length) return current;
  const seen = new Set(current.map((row) => row?.id).filter(Boolean));
  const merged = [...current];
  for (const row of next) {
    if (!row?.id || seen.has(row.id)) continue;
    seen.add(row.id);
    merged.push(row);
  }
  return merged;
}

export function importHistoryActionLabel(actionKind, t) {
  if (actionKind === 'create') return t('admin.prospection.importHistory.action.create');
  if (actionKind === 'update') return t('admin.prospection.importHistory.action.update');
  return t('admin.prospection.importHistory.action.unknown');
}

export function importHistoryChangedFieldsSummary(changedFields, t) {
  if (!Array.isArray(changedFields) || !changedFields.length) {
    return t('admin.prospection.importHistory.changedFields.empty');
  }
  const labels = changedFields.map((field) => importPreviewTopFieldLabel(field, t));
  return t('admin.prospection.importHistory.changedFields.summary')
    .replace('{count}', String(labels.length))
    .replace('{fields}', labels.join(', '));
}

export function importHistorySectionOutcomeLabel(outcome, t) {
  if (!outcome) return null;
  const key = `admin.prospection.importHistory.outcome.${outcome}`;
  const label = t(key);
  return label === key ? null : label;
}

export function importHistoryDispositionLabel(disposition, t) {
  if (!disposition) return t('admin.prospection.importHistory.disposition.unknown');
  const key = `admin.prospection.importHistory.disposition.${disposition}`;
  const label = t(key);
  return label === key ? t('admin.prospection.importHistory.disposition.unknown') : label;
}

/** @deprecated Prefer importHistoryDispositionLabel */
export function importHistoryApplicationLabel(status, t) {
  if (status === 'applied' || status === 'replaced' || status === 'enriched' || status === 'modified') {
    return importHistoryDispositionLabel(status === 'applied' ? 'replaced' : status, t);
  }
  if (status === 'not_applied' || status === 'preserved') {
    return importHistoryDispositionLabel('preserved', t);
  }
  if (status === 'unchanged') return importHistoryDispositionLabel('unchanged', t);
  return importHistoryDispositionLabel('unknown', t);
}

function centsLabel(value, language, t, unavailable) {
  if (value === null) return unavailable;
  if (value === 0) return formatAdminProspectCentsLabel(0, language, t);
  return formatAdminProspectCentsLabel(value, language, t) || unavailable;
}

/**
 * Build readable rows for a recorded history snapshot without inventing values.
 * Disposition labels describe merge outcomes; preserved values are not shown as rejected incoming.
 */
export function buildImportHistoryRecordedRows(entry, language, t) {
  const unavailable = formatAdminProspectUnavailable(t);
  const recorded = entry?.snapshot?.recorded || {};
  const dispositions = entry?.snapshot?.sectionDispositions || {};
  const rows = [];

  const pushSection = (key, titleKey, fields, disposition) => {
    if (!fields.length) return;
    rows.push({
      key,
      title: t(titleKey),
      application: importHistoryDispositionLabel(disposition, t),
      applicationStatus: disposition || 'unknown',
      disposition: disposition || 'unknown',
      fields,
    });
  };

  const hasOwn = (obj, prop) => Object.prototype.hasOwnProperty.call(obj || {}, prop);

  const profile = recorded.latestImportedPropertyProfile;
  if (profile) {
    const fields = [];
    if (profile.buildingCategory) {
      fields.push({
        label: t('admin.prospection.importedProperty.buildingCategory'),
        value: profile.buildingCategory,
      });
    }
    if (profile.constructionType) {
      fields.push({
        label: t('admin.prospection.importedProperty.constructionType'),
        value: profile.constructionType,
      });
    }
    if (profile.numberOfUnits != null) {
      fields.push({
        label: t('admin.prospection.importedProperty.numberOfUnits'),
        value: formatAdminProspectUnits(profile.numberOfUnits),
      });
    }
    if (profile.numberOfFloors != null) {
      fields.push({
        label: t('admin.prospection.importedProperty.numberOfFloors'),
        value: formatAdminProspectUnits(profile.numberOfFloors),
      });
    }
    if (profile.constructionYear != null) {
      fields.push({
        label: t('admin.prospection.importedProperty.constructionYear'),
        value: String(profile.constructionYear),
      });
    }
    if (profile.landArea) {
      fields.push({
        label: t('admin.prospection.importedProperty.landArea'),
        value: formatAdminProspectLandArea(profile.landArea, language, t) || unavailable,
      });
    }
    pushSection(
      'profile',
      'admin.prospection.importedProperty.section.characteristics',
      fields,
      dispositions.latestImportedPropertyProfile || 'unknown',
    );
  }

  const assessment = recorded.latestMunicipalAssessment;
  if (assessment) {
    const fields = [];
    if (hasOwn(assessment, 'totalCents')) {
      fields.push({
        label: t('admin.prospection.importedProperty.assessment.total'),
        value: centsLabel(assessment.totalCents, language, t, unavailable),
      });
    }
    if (hasOwn(assessment, 'landCents')) {
      fields.push({
        label: t('admin.prospection.importedProperty.assessment.land'),
        value: centsLabel(assessment.landCents, language, t, unavailable),
      });
    }
    if (hasOwn(assessment, 'buildingCents')) {
      fields.push({
        label: t('admin.prospection.importedProperty.assessment.building'),
        value: centsLabel(assessment.buildingCents, language, t, unavailable),
      });
    }
    if (hasOwn(assessment, 'referenceYear') && assessment.referenceYear != null) {
      fields.push({
        label: t('admin.prospection.importedProperty.assessment.referenceYear'),
        value: String(assessment.referenceYear),
      });
    }
    if (hasOwn(assessment, 'referenceDate') && assessment.referenceDate) {
      fields.push({
        label: t('admin.prospection.importedProperty.assessment.referenceDate'),
        value: formatImportPreviewDateOnly(assessment.referenceDate, language) || assessment.referenceDate,
      });
    }
    const outcome = entry?.snapshot?.sectionOutcomes?.latestMunicipalAssessment;
    if (outcome) {
      fields.push({
        label: t('admin.prospection.importHistory.outcome.label'),
        value: importHistorySectionOutcomeLabel(outcome, t) || outcome,
      });
    }
    pushSection(
      'assessment',
      'admin.prospection.importedProperty.section.assessment',
      fields,
      dispositions.latestMunicipalAssessment || 'unknown',
    );
  }

  const activity = recorded.latestRegistryActivity;
  if (activity) {
    const fields = [];
    if (activity.activityType) {
      fields.push({
        label: t('admin.prospection.importedProperty.registry.activityType'),
        value:
          adminRegistryActivityTypeLabel(activity.activityType, t) ||
          activity.originalLabel ||
          activity.activityType,
      });
    }
    if (activity.originalLabel) {
      fields.push({
        label: t('admin.prospection.importedProperty.registry.originalLabel'),
        value: activity.originalLabel,
      });
    }
    if (activity.activityDate) {
      fields.push({
        label: t('admin.prospection.importedProperty.registry.activityDate'),
        value: formatImportPreviewDateOnly(activity.activityDate, language) || activity.activityDate,
      });
    }
    if (hasOwn(activity, 'considerationCents')) {
      fields.push({
        label: t('admin.prospection.importedProperty.registry.consideration'),
        value: centsLabel(activity.considerationCents, language, t, unavailable),
      });
    }
    if (activity.registrationNumber) {
      fields.push({
        label: t('admin.prospection.importedProperty.registry.registrationNumber'),
        value: activity.registrationNumber,
      });
    }
    if (activity.transferredPercentage != null) {
      fields.push({
        label: t('admin.prospection.importedProperty.registry.transferredPercentage'),
        value: `${activity.transferredPercentage}%`,
      });
    }
    const outcome = entry?.snapshot?.sectionOutcomes?.latestRegistryActivity;
    if (outcome) {
      fields.push({
        label: t('admin.prospection.importHistory.outcome.label'),
        value: importHistorySectionOutcomeLabel(outcome, t) || outcome,
      });
    }
    pushSection(
      'registry',
      'admin.prospection.importedProperty.section.registry',
      fields,
      dispositions.latestRegistryActivity || 'unknown',
    );
  }

  const cadastralFields = [];
  const lots = recorded.lotNumbers || profile?.lotNumbers;
  if (Array.isArray(lots) && lots.length) {
    cadastralFields.push({
      label: t('admin.prospection.importedProperty.lotNumbers'),
      value: lots.join(', '),
    });
  }
  if (recorded.cadastralDivision || profile?.cadastralDivision) {
    cadastralFields.push({
      label: t('admin.prospection.importedProperty.cadastralDivision'),
      value: recorded.cadastralDivision || profile.cadastralDivision,
    });
  }
  if (recorded.municipalRollNumber || profile?.municipalRollNumber) {
    cadastralFields.push({
      label: t('admin.prospection.importedProperty.municipalRollNumber'),
      value: recorded.municipalRollNumber || profile.municipalRollNumber,
    });
  }
  if (recorded.numberOfUnits != null && !profile) {
    cadastralFields.push({
      label: t('admin.prospection.importedProperty.numberOfUnits'),
      value: formatAdminProspectUnits(recorded.numberOfUnits),
    });
  }
  if (cadastralFields.length) {
    pushSection(
      'cadastral',
      'admin.prospection.importedProperty.section.cadastral',
      cadastralFields,
      'unknown',
    );
  }

  if (Array.isArray(recorded.owners) && recorded.owners.some(Boolean)) {
    const fields = recorded.owners.filter(Boolean).map((owner, index) => ({
      label: t('admin.prospection.importHistory.ownerSlot').replace('{n}', String(index + 1)),
      value: [owner.name, owner.mailingAddress, owner.city, owner.province, owner.postalCode]
        .filter(Boolean)
        .join(' · '),
    }));
    pushSection('owners', 'admin.prospection.import.field.owners', fields, dispositions.owners || 'unknown');
  } else if (recorded.ownerName || recorded.ownerMailingAddress) {
    const fields = [];
    if (recorded.ownerName) {
      fields.push({
        label: t('admin.prospection.field.ownerName'),
        value: recorded.ownerName,
      });
    }
    if (recorded.ownerMailingAddress) {
      fields.push({
        label: t('admin.prospection.field.ownerMailingAddress'),
        value: recorded.ownerMailingAddress,
      });
    }
    pushSection('owner', 'admin.prospection.section.owner', fields, 'unknown');
  }

  return rows;
}

/** @deprecated Prefer buildImportHistoryRecordedRows */
export function buildImportHistoryIncomingRows(entry, language, t) {
  return buildImportHistoryRecordedRows(entry, language, t);
}

export function formatImportHistoryImportedAt(value, language) {
  return formatAdminDate(value, language);
}

export function formatImportHistorySource(sourceType, t) {
  return adminImportProvenanceSourceLabel(sourceType, t) || t('admin.prospection.importHistory.source.unknown');
}
